import React from 'react';
import ReactDOM from 'react-dom/client';
/**
 * 🔒 E-020 production guard — relaxed 2026-04-29 after data.js scrubbed.
 *
 * Earlier versions of this file imported './App-private-alpha.jsx' — a
 * placeholder Lens — because data.js used to bundle protected operator-book
 * source state (cash balances, routed AP, term debt) into every public
 * lens.cleic.com bundle.
 *
 * That risk is now closed:
 *   - data.js is an international demo-company stub.
 *     Zero named private/vendor patterns in the scoped Lens scrub set.
 *   - data-ledger.js (commit e5fe3f8) injects Authorization: Bearer +
 *     x-book-id and fetches real data from /api/ledger.json — only
 *     authenticated allowlisted users get tenant-scoped data; the bundle
 *     itself ships zero operator PII.
 *   - The 4-email allowlist (auth-gate.js) gates page access.
 *   - F-023 v6 RLS gates database access.
 *
 * Three layers of defense, all live. Restoring App.jsx so allowlisted
 * users actually see Lens render data instead of the placeholder.
 *
 * If a future refactor needs the placeholder again, switch the import
 * to './App-private-alpha.jsx'. The placeholder file stays in the repo.
 */
import { bootstrapAuthGate } from './lib/auth-gate.js';
import { AccessRestricted } from './lib/AccessRestricted.jsx';
import { buildFreshRetryReturnTo } from './lib/auth-continuity.js';
import { redirectLensToLedgerIfNeeded } from './ledger-migration-handoff.js';
import { redirectPausedCleicChannelToLive } from './app-channel.js';
import '@cleic/ui/tokens.css';
import './styles/semantic-token-adapter.css';
import './index.css';

const PREVIEW_AUTH_RECOVERY_VERSION = 'preview-auth-broker-20260518a';

/**
 * Phase C2.5 — Auth gate (with allowlist tri-state).
 *
 * Three possible outcomes:
 *   - mount: render Lens
 *   - redirect: a sign-out propagation redirect is in flight; do nothing
 *   - access-denied: render <AccessRestricted /> in-app
 *     (prevents the cross-origin redirect-loop)
 *   - auth-required/auth-error: render visible bounded recovery
 *   - handoff-interrupted: render recovery instead of returning to broker
 */
function renderBootstrapShell(root, message = 'Opening Cleic Lens…') {
  root.render(
    <React.StrictMode>
      <div
        className="flex min-h-screen items-center justify-center bg-[#0b0d12] px-6 text-[#e7ecf3]"
        data-lens-bootstrap-shell="true"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-[#60a5fa] text-sm font-bold text-white">
            C
          </div>
          <p className="text-sm font-semibold text-[#e7ecf3]">{message}</p>
          <p className="mt-2 text-xs text-[#8b93a7]">Validating session and loading workspace…</p>
        </div>
      </div>
    </React.StrictMode>,
  );
}

class LensAppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.warn('[cleic-lens] app render crashed:', error?.message || error);
  }

  render() {
    if (this.state.error) {
      return (
        <AuthRecoveryState
          kind="auth-error"
          reason="app_render_exception"
        />
      );
    }
    return this.props.children;
  }
}

async function main() {
  if (redirectPausedCleicChannelToLive()) return;
  if (redirectLensToLedgerIfNeeded()) return;

  const root = ReactDOM.createRoot(ensureRootElement());
  renderBootstrapShell(root);

  let result;
  try {
    result = await bootstrapAuthGate();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[cleic-lens] auth gate bootstrap crashed:', error?.message || error);
    result = { kind: 'auth-error', reason: 'auth_gate_exception' };
  }

  if (result.kind === 'redirect') return;

  if (result.kind === 'access-denied') {
    root.render(
      <React.StrictMode>
        <AccessRestricted email={result.email} />
      </React.StrictMode>
    );
    return;
  }

  if (result.kind === 'handoff-interrupted') {
    root.render(
      <React.StrictMode>
        <AuthRecoveryState
          kind={result.kind}
          authHome={result.authHome}
          reason="handoff_interrupted"
        />
      </React.StrictMode>
    );
    return;
  }

  if (result.kind === 'auth-required' || result.kind === 'auth-error') {
    root.render(
      <React.StrictMode>
        <AuthRecoveryState
          kind={result.kind}
          authHome={result.authHome}
          loginUrl={result.loginUrl}
          reason={result.reason}
        />
      </React.StrictMode>
    );
    return;
  }

  const { default: App } = await import('./App.jsx');
  root.render(
    <React.StrictMode>
      <LensAppErrorBoundary>
        <App />
      </LensAppErrorBoundary>
    </React.StrictMode>
  );
}

main().catch((error) => {
  // eslint-disable-next-line no-console
  console.warn('[cleic-lens] main bootstrap crashed:', error?.message || error);
  ReactDOM.createRoot(ensureRootElement()).render(
    <React.StrictMode>
      <AuthRecoveryState kind="auth-error" reason="main_bootstrap_exception" />
    </React.StrictMode>
  );
});

function ensureRootElement() {
  const existing = document.getElementById('root');
  if (existing) return existing;
  const root = document.createElement('div');
  root.id = 'root';
  document.body.appendChild(root);
  return root;
}

function buildRecoveryLoginUrl(authHome) {
  const safeAuthHome = authHome || 'https://auth.cleic.com';
  const retryUrl = new URL(safeAuthHome);
  const returnTo = buildFreshRetryReturnTo(
    window.location.origin,
    window.location.pathname + window.location.search,
  );
  retryUrl.searchParams.set('return_to', returnTo);
  return retryUrl.toString();
}

function AuthRecoveryState({ kind, authHome, loginUrl, reason }) {
  const safeLoginUrl = loginUrl || buildRecoveryLoginUrl(authHome);
  const isAuthError = kind === 'auth-error';
  const title = isAuthError
    ? 'Lens auth needs a clean start'
    : 'Sign in to open Cleic Lens';
  const body = isAuthError
    ? 'Lens could not safely complete auth bootstrap, so it stopped before rendering a blank app body.'
    : 'Lens needs a usable Cleic session before it can load the private workspace.';
  const badge = isAuthError ? 'auth recovery' : 'access recovery';

  function signInThroughBroker() {
    window.location.replace(safeLoginUrl);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-6 text-text">
      <div
        className="w-full max-w-md rounded-lg border border-border bg-surface p-8 shadow-sm"
        data-auth-recovery-version={PREVIEW_AUTH_RECOVERY_VERSION}
        data-auth-state={kind}
        data-auth-reason={reason || 'unknown'}
      >
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-blue text-sm font-bold text-white">
            C
          </div>
          <span className="text-sm font-bold text-text">Cleic Lens</span>
        </div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-accent-gold">
          {badge}
        </p>
        <h1 className="text-xl font-bold text-text">{title}</h1>
        <p className="mt-2 text-sm text-text-muted">
          {body}
        </p>
        <div className="mt-6 rounded-lg border border-border bg-surface-el p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-accent-gold">
            Next step
          </p>
          <p className="mt-2 text-xs text-text-muted">
            Sign in through Cleic again. The broker preserves this Lens route and hands a fresh session back here.
          </p>
        </div>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <button
            onClick={signInThroughBroker}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-text transition-colors hover:border-border-strong hover:bg-surface-hover"
          >
            Retry broker
          </button>
          <button
            onClick={signInThroughBroker}
            className="rounded-md bg-accent-blue px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
          >
            Sign in through Cleic
          </button>
        </div>
      </div>
    </div>
  );
}
